import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, Bell, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FadeIn, Eyebrow } from "./primitives";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const datePartsInTz = (instant, tz) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = {};
  for (const part of dtf.formatToParts(instant)) parts[part.type] = part.value;
  return { y: +parts.year, m: +parts.month, d: +parts.day };
};

const tzOffsetMinutes = (instant, tz) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = {};
  for (const part of dtf.formatToParts(instant)) parts[part.type] = part.value;
  const asUtc = Date.UTC(
    +parts.year,
    +parts.month - 1,
    +parts.day,
    +parts.hour % 24,
    +parts.minute,
    +parts.second
  );
  return (asUtc - instant.getTime()) / 60000;
};

const zonedTimeToUtc = (y, m, d, hour, tz) => {
  const guess = Date.UTC(y, m - 1, d, hour, 0, 0);
  let utc = guess - tzOffsetMinutes(new Date(guess), tz) * 60000;
  utc = guess - tzOffsetMinutes(new Date(utc), tz) * 60000;
  return new Date(utc);
};

const getNextRelease = (weekday, hour, tz) => {
  const now = new Date();
  let zone = tz;
  try {
    Intl.DateTimeFormat("en-US", { timeZone: zone });
  } catch {
    zone = "Africa/Cairo";
  }
  const { y, m, d } = datePartsInTz(now, zone);
  for (let i = 0; i < 8; i++) {
    const day = new Date(Date.UTC(y, m - 1, d + i));
    if (day.getUTCDay() !== weekday) continue;
    const instant = zonedTimeToUtc(
      day.getUTCFullYear(),
      day.getUTCMonth() + 1,
      day.getUTCDate(),
      hour,
      zone
    );
    if (instant > now) return instant;
  }
  return zonedTimeToUtc(y, m, d + 7, hour, zone);
};

const useCountdown = (target) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return useMemo(() => {
    const diff = Math.max(0, target - now);
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      done: diff <= 0,
    };
  }, [now, target]);
};

const TimeCell = ({ value, label }) => (
  <div className="border border-bone/10 bg-coal px-3 py-5 text-center">
    <p className="font-display text-3xl font-light text-bone sm:text-4xl">
      {String(value).padStart(2, "0")}
    </p>
    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">{label}</p>
  </div>
);

export const NextWeek = ({ site, onSiteRefresh }) => {
  const weekday = site?.release_weekday ?? 0;
  const hour = site?.release_hour ?? 17;
  const tz = site?.release_tz || "Africa/Cairo";
  const tzCity = tz.split("/").pop().replace(/_/g, " ");
  const target = useMemo(() => {
    if (site?.next_premiere_at) {
      const parsed = new Date(site.next_premiere_at);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return getNextRelease(weekday, hour, tz);
  }, [site?.next_premiere_at, weekday, hour, tz]);
  const countdown = useCountdown(target);
  const revealed = site?.revealed_episode || null;
  const zeroFired = useRef(false);

  useEffect(() => {
    if (countdown.done && !zeroFired.current) {
      zeroFired.current = true;
      const timer = setTimeout(() => {
        onSiteRefresh?.();
        zeroFired.current = false;
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [countdown.done, onSiteRefresh]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscribers", { email: email.trim() });
      toast.success(
        data?.message === "already subscribed"
          ? "You're already on the list."
          : "You're on the list — we'll write when it premieres."
      );
      setEmail("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add you. Try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="next-week"
      data-testid="next-week-section"
      className="border-t border-bone/10 bg-coal"
    >
      <div className="mx-auto max-w-6xl px-6 py-24">
        <FadeIn>
          <Eyebrow>Coming soon</Eyebrow>
        </FadeIn>

        <div className="mt-12 grid gap-16 md:grid-cols-2 md:items-center">
          <FadeIn delay={0.1}>
            {revealed ? (
              <h2
                data-testid="revealed-heading"
                className="font-display text-3xl font-light leading-snug tracking-tight text-bone sm:text-4xl"
              >
                Now live: <span className="italic text-ruby">{revealed.title}</span>
                {revealed.film && (
                  <>
                    <br />
                    <span className="text-smoke">— {revealed.film}</span>
                  </>
                )}
              </h2>
            ) : (
              <h2 className="font-display text-3xl font-light leading-snug tracking-tight text-bone sm:text-4xl">
                Next week:{" "}
                <span className="redacted px-2 font-medium" data-testid="redacted-title">
                  The Next Episode Title
                </span>
                <br />
                <span className="text-smoke">— and we're not telling.</span>
              </h2>
            )}
            <p
              data-testid="teaser-cryptic-line"
              className="mt-8 max-w-md text-base font-light leading-relaxed text-zinc-400"
            >
              {site?.cryptic_line ||
                "One scene. One question. We will not say which film — you will know it when you hear it."}
            </p>
            <p className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-smoke">
              <CalendarClock size={14} className="text-ruby" />
              Every {WEEKDAYS[weekday]} at {String(hour).padStart(2, "0")}:00 · {tzCity} time
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="border border-bone/10 bg-noir p-8 sm:p-10">
              <div className="flex items-center justify-between gap-4">
                <p
                  data-testid="teaser-release-label"
                  className="font-mono text-xs uppercase tracking-[0.25em] text-bone"
                >
                  {site?.release_label || "New episode every week"}
                </p>
                <span className="h-2 w-2 animate-pulse rounded-full bg-ruby" aria-hidden="true" />
              </div>

              {revealed ? (
                <div data-testid="now-live-panel" className="mt-8 border border-ruby/40 bg-coal p-6 text-center">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-ruby-bright">
                    The wait is over
                  </p>
                  <p className="mt-3 font-display text-xl leading-snug text-bone">
                    {revealed.title}
                  </p>
                  {revealed.watch_url ? (
                    <a
                      href={revealed.watch_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="watch-new-episode-link"
                      className="mt-5 inline-flex items-center gap-2 bg-ruby px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-ruby-bright"
                    >
                      <Play size={13} /> Watch the new episode
                    </a>
                  ) : (
                    <a
                      href="#latest"
                      data-testid="watch-new-episode-link"
                      className="mt-5 inline-flex items-center gap-2 bg-ruby px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-ruby-bright"
                    >
                      <Play size={13} /> Hear it now
                    </a>
                  )}
                </div>
              ) : (
                <div data-testid="countdown-timer" className="mt-8 grid grid-cols-4 gap-2">
                  <TimeCell value={countdown.days} label="Days" />
                  <TimeCell value={countdown.hours} label="Hours" />
                  <TimeCell value={countdown.minutes} label="Min" />
                  <TimeCell value={countdown.seconds} label="Sec" />
                </div>
              )}

              <div className="mt-8 border-t border-bone/10 pt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
                  First to know when it premieres
                </p>
                <form onSubmit={handleNotify} data-testid="notify-form" className="mt-4 flex gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="notify-email-input"
                    placeholder="your@email.com"
                    aria-label="Email for premiere notifications"
                    className="min-w-0 flex-1 border border-bone/15 bg-coal px-4 py-3 text-sm font-light text-bone placeholder:text-zinc-600 focus:border-ruby focus:outline-none focus:ring-1 focus:ring-ruby"
                  />
                  <button
                    type="submit"
                    disabled={subscribing}
                    data-testid="notify-submit-button"
                    className="flex shrink-0 items-center gap-2 bg-ruby px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-ruby-bright disabled:opacity-50"
                  >
                    <Bell size={13} /> {subscribing ? "…" : "Notify me"}
                  </button>
                </form>
                <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-zinc-600">
                  One email per premiere. Nothing else. The title stays in the dark until then.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
